// demoScript.js — all content for the /demo page.
// Self-contained: no backend, no API calls. Quarantined from the real chat components.
// Wording is final copy supplied by the site owner — do not edit casually.

export const AVATAR_NAME = 'Robert';
export const AVATAR_FULL_NAME = 'Robert Mitchell';

// ── One-on-one chat ────────────────────────────────────────────────

export const OPENING_MESSAGE =
  "Well, hello there. Ellie always said I never met a conversation I couldn't stretch another twenty minutes. Consider yourself warned. What's on your mind?";

// Suggested-question chips in round order (lighter → more personal). The UI
// flattens these into one pool and always shows the first three unused chips,
// refilling a slot as soon as its chip is used. Every chip verified
// against KEYWORD_RESPONSES so none falls through to the fallback.
export const CHIP_ROUNDS = [
  ['About his first job', 'Saturday mornings at the lake', 'Tell me about Ellie'],
  ['The kitchen table', 'His famous pancakes', "What he's proud of"],
  ['Tell him you miss him', 'Advice for a hard day', 'Is he real?'],
  ['What matters most', 'Tell me a joke', 'Growing up on the farm'],
];

// Matched in this order; first match wins. Matching is lowercase substring.
export const KEYWORD_RESPONSES = [
  {
    key: 'grief',
    triggers: ['miss you', 'miss him', 'gone', 'died', 'passed', 'wish you were', 'lost you'],
    response:
      "I know. Saturday mornings at the lake are the ones I keep going back to — the water so quiet you could hear the coffee going cold. You don't have to say anything clever. Just sit with me a while.",
  },
  {
    key: 'are-you-real',
    triggers: ['real', 'ai', 'robot', 'fake', 'actually you', 'computer', 'chatbot'],
    response:
      "No — I'm not Robert, not really. I'm built from his words: his letters, the notes he left in margins, fifty years of the way he talked. Think of me as an echo that learned to answer back. The remembering is real, even if I'm not.",
  },
  {
    key: 'work',
    triggers: ['job', 'work', 'career', 'carpenter', 'build', 'built'],
    response:
      "Thirty-four years a carpenter, and I'd have done thirty-four more. Built half the porches on Maple Street and one kitchen table that outlasted three dishwashers and every argument this family ever had. Measure twice, cut once — and when it still comes out crooked, blame the wood.",
  },
  {
    key: 'table',
    triggers: ['table', 'kitchen table'],
    response:
      "That table's maple — cut from the tree that came down in the '89 storm. Every scratch in it is somebody's homework or somebody's elbows. Don't you ever let anyone refinish it.",
  },
  {
    key: 'fishing',
    triggers: ['fish', 'fishing', 'lake', 'harmon', 'outdoors'],
    response:
      "Lake Harmon, Saturday mornings, before the sun got ambitious. The fish and I had an understanding: I'd show up, they'd ignore me, and we'd both call it a good day. It was never really about the fish.",
  },
  {
    key: 'ellie',
    triggers: ['ellie', 'eleanor', 'grandma', 'grandmother', 'wife', 'marriage', 'love'],
    response:
      "Fifty-two years with Ellie, and here's the whole secret: I never once won an argument, and I never once went to bed wanting to be anywhere else. Marry someone who laughs at your bad jokes. Then keep making them.",
  },
  {
    key: 'advice',
    triggers: ['advice', 'hard', 'tough', 'struggling', 'difficult', 'help me', 'what should i'],
    response:
      "Hard days are like warped boards — you don't force them straight all at once. You work them slow. Do the next small thing, then the one after. And eat something, for Pete's sake — nobody ever solved anything hungry.",
  },
  {
    key: 'pancakes',
    triggers: ['pancake', 'cook', 'food', 'breakfast', 'kitchen'],
    response:
      "I burned every pancake I ever made, and your grandmother ate every single one like it was fine dining. That's love — that, and she made me do the dishes after.",
  },
  {
    key: 'music',
    triggers: ['music', 'song', 'sing', 'listen'],
    response:
      'Johnny Cash, loud, in the workshop — that was mine. Your grandmother preferred Sinatra, so Sundays were Sinatra. A good marriage is mostly a negotiated playlist.',
  },
  {
    key: 'joke',
    triggers: ['joke', 'funny', 'laugh', 'humor'],
    response:
      'Why do carpenters make terrible liars? Because eventually, everything they say gets nailed down. Ellie groaned at that one for forty years. Never once stopped me.',
  },
  {
    key: 'family',
    triggers: ['family', 'kids', 'children', 'grandchildren', 'proud'],
    response:
      "Proud doesn't cover it. I built things my whole life meant to outlast me — and not one of them holds a candle to how this family turned out. That's the one thing I can't take credit for, and the one I'd brag about longest.",
  },
  {
    key: 'childhood',
    triggers: ['childhood', 'growing up', 'young', 'farm', 'kid'],
    response:
      "Grew up on a farm outside town — up before the roosters, which is why I've never trusted them. We didn't have much, but my father could fix anything with baling wire and stubbornness. I inherited the stubbornness.",
  },
  {
    key: 'retirement',
    triggers: ['retire', 'retirement', 'old', 'aging'],
    response:
      'Retirement — ha. I just stopped getting paid for it. The workshop light was on at six every morning till the end. A man needs something to sand.',
  },
  {
    key: 'life',
    triggers: ['matters', 'meaning', 'life', 'happy', 'grateful', 'wisdom'],
    response:
      "At the end of it all? The table full on Sundays. Ellie humming in the kitchen. Somebody's kid asleep on my shoulder during the ballgame. That's the whole fortune. The rest is sawdust.",
  },
  {
    key: 'small-talk',
    triggers: ['weather', 'how are you', "what's up", 'hello', 'hi', 'hey'],
    response:
      "Can't complain — can't complain. Now quit stalling. What did you really come to ask me?",
  },
];

export const FALLBACK_RESPONSE =
  "Now that's one I'd want to sit with a minute — pour a cup of coffee and do it properly. Ask me about the lake, or the table, or Ellie, and I'll talk your ear off.";

// Shown once, beneath the first fallback only, in small quiet text.
export const FALLBACK_CAPTION =
  "This preview knows a handful of Robert's stories. A real Avatar is built from everything your loved one ever wrote — and answers all of it.";

// ── Family Chat tab ────────────────────────────────────────────────

export const FAMILY_CHAT = {
  participants: ['Robert Mitchell', 'Susan (daughter)', 'Tommy (grandson)', 'You'],
  seedMessages: [
    { sender: 'Susan', text: 'Dad, Tommy got into the engineering program. First one in the family.' },
    {
      sender: 'Robert',
      text: "Engineering! Somebody in this family is finally going to learn why my birdhouses leaned left. Tommy, I'm proud of you — the first one, but not the last.",
    },
    { sender: 'Tommy', text: "I'm keeping the birdhouse on my desk, Grandpa. Lean and all." },
  ],
  // Shown once, ~1200ms after Robert's reply to the visitor's FIRST message in this tab.
  interjection: { sender: 'Susan', text: 'Some things never change — he still gets the last word.' },
  interjectionDelayMs: 1200,
};

// ── Improve modal ──────────────────────────────────────────────────

export const IMPROVE_MODAL = {
  title: 'Help Robert Remember',
  confidenceLabel: "Robert's Self-Knowledge",
  confidenceStart: 62,
  confidencePerAnswer: 3,
  confidenceCap: 71,
  stats: [
    { label: 'Documents shared', value: 12 },
    { label: 'Conversations', value: 34 },
    { label: 'Memories added', value: 7 },
  ],
  questions: [
    {
      id: 'name',
      question: 'Now, remind an old man — who are you to me, and what do I call you?',
      ack: "Of course. Some things you don't forget — you just like hearing them again.",
    },
    {
      id: 'memory',
      question:
        "Tell me one small moment of ours you keep coming back to. Doesn't have to be a big one — the small ones hold the most.",
      ack: "There it is. You handed me something I'd forgotten I was missing. Thank you for that.",
    },
    {
      id: 'pancakes',
      question: "Now — lighter question, before you go. My pancakes: charcoal, or 'well-done'?",
      ack: 'Ha! Ellie used to say the smoke alarm was my kitchen timer. You keep that honesty — it suits you.',
    },
  ],
};

// One-time payoff: Robert's next one-on-one message after the improve modal
// closes, once Q1 and Q2 have been answered. Deliberately not personalized —
// visitor input is never echoed back in Robert's replies.
export const SESSION_PAYOFF =
  'There you are. It does an old man good, being remembered. Sit with me a minute.';

// ── End of demo ────────────────────────────────────────────────────

// After this many visitor-sent messages, total across both tabs (chip clicks count).
export const MESSAGE_LIMIT = 12;

export const END_OF_DEMO = {
  heading: 'This was a small taste.',
  body:
    "Robert is a sample — a few stories, written down. When you create an Avatar of someone you love, it's built from their real words: letters, emails, journals. And it grows the more your family shares.",
  buttonLabel: 'Create Your First Avatar',
  buttonLink: '/signup',
};

// Caption shown above the suggested-question chips.
export const CHIPS_CAPTION =
  'Choose a question to ask Robert — in the full experience, you can ask anything.';

// Placeholder for the visible-but-disabled message input.
export const INPUT_PLACEHOLDER = 'Typing is enabled in the full experience';

// Typing indicator delay range for Robert's replies (ms).
export const TYPING_DELAY_MIN_MS = 800;
export const TYPING_DELAY_MAX_MS = 1500;
